/* ============================================================
   IDEAS CAFE — <ideas-add>

   Wraps any button carrying [data-add] and a [data-variant-id],
   posts it to /cart/add.js, and publishes the result through the
   shared window.IdeasCart contract so the header count, the cart
   rail and the toast all update from one source.

   The button reports state on itself via data-state so styling
   stays in CSS:  busy | added | error
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('ideas-add')) return;

  var RESET_MS = 2200;

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class IdeasAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-add]');
      if (!this.button) return;

      this.label = this.querySelector('[data-add-label]') || this.button;
      this.original = this.label.textContent.trim();

      // Feedback copy comes from the theme's locale files via data
      // attributes — the element must not hardcode English.
      this.busyLabel = this.button.dataset.labelBusy || this.original;
      this.doneLabel = this.button.dataset.labelDone || this.original;

      this._onClick = this._onClick.bind(this);
      this.button.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onClick);
      if (this._resetTimer) clearTimeout(this._resetTimer);
    }

    _setState(state, text) {
      if (state) this.button.dataset.state = state;
      else delete this.button.dataset.state;
      if (text != null) this.label.textContent = text;
    }

    _scheduleReset() {
      var self = this;
      if (this._resetTimer) clearTimeout(this._resetTimer);
      this._resetTimer = setTimeout(function () {
        self._setState(null, self.original);
      }, RESET_MS);
    }

    _onClick(event) {
      event.preventDefault();
      // The card is wrapped in a stretched link; without this the add
      // would also navigate to the product page.
      event.stopPropagation();

      var id = this.button.dataset.variantId;
      if (!id || this.button.dataset.state === 'busy') return;

      var quantity = parseInt(this.button.dataset.quantity, 10) || 1;
      var self = this;

      this._setState('busy', this.busyLabel);

      /* No fulfilment on the line.

         Delivery, pickup and dine-in are a choice about the basket, not
         about this product, so they stay in the cart attributes that
         <ideas-order-type> writes and are never copied onto a line. */
      var line = { id: id, quantity: quantity };

      fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [line] })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            // Shopify returns 422 with a description for stock and
            // validation failures — surface that, not a generic error.
            if (!res.ok) {
              throw new Error(body.description || body.message || 'Could not add to cart');
            }
            return body;
          });
        })
        .then(function () {
          self._setState('added', self.doneLabel);
          self._scheduleReset();

          self.dispatchEvent(
            new CustomEvent('ideas:item:added', {
              bubbles: true,
              detail: {
                variantId: id,
                quantity: quantity,
                title: self.button.dataset.productTitle || ''
              }
            })
          );

          // Re-read the cart so every listener gets totals, not just
          // the line that was added.
          if (window.IdeasCart) window.IdeasCart.refresh();
        })
        .catch(function (err) {
          console.warn('[ideas] add to cart failed', err);
          // Restore the original label rather than invent an error
          // string: the [data-state="error"] styling already signals
          // the failure, and this keeps the element free of hardcoded
          // English.
          self._setState('error', self.original);
          self._scheduleReset();
        });
    }
  }

  customElements.define('ideas-add', IdeasAdd);
})();
